import { useTranslation } from 'react-i18next'

import { EmptyPanel } from '@/components/px/EmptyPanel'
import { ErrorPanel } from '@/components/px/ErrorPanel'
import { LoadingPanel } from '@/components/px/LoadingPanel'
import { SectionLabel } from '@/components/px/SectionLabel'
import { StatusPill } from '@/components/px/StatusPill'
import { useAppSelector } from '@/store'
import { selectSessionAnalysis, selectSessionError, selectSessionLoading } from '../slices/intakesSlice'

/**
 * The selected session's real analysis (AC-4): the framework outputs it was
 * expected to capture, and the scoring the server returned for each.
 *
 * Attribution and progress (decision 2 of the plan) stay SECONDARY — this
 * ticket's slice does not carry a conversation id independent of the
 * assignee row, so neither is fetched here; `intakesService` already exposes
 * `getConversationAttribution` / `getConversationProgress` for the follow-up
 * that wires a conversation-id-bearing surface to them.
 */
export function SessionAnalysisPanel() {
  const { t } = useTranslation('projectWorkspace')
  const loading = useAppSelector(selectSessionLoading)
  const error = useAppSelector(selectSessionError)
  const analysis = useAppSelector(selectSessionAnalysis)

  return (
    <section
      aria-label={t('intakes.analysis.panelTitle')}
      className="rounded-lg border border-border bg-card px-4 py-3.5"
    >
      <SectionLabel>{t('intakes.analysis.panelTitle')}</SectionLabel>
      <div className="mt-2">
        {loading ? (
          <LoadingPanel label={t('loading')} rows={2} />
        ) : error !== null ? (
          <ErrorPanel error={{ status: error.status ?? 0, backendKey: error.code, message: error.message }} />
        ) : analysis === null || analysis.outputs.length === 0 ? (
          <EmptyPanel title={t('intakes.analysis.emptyTitle')} body={t('intakes.analysis.emptyBody')} />
        ) : (
          <div data-testid="session-analysis">
            {analysis.scoring !== null && (
              <p className="v-figure text-sm tabular-nums text-foreground">
                {t('intakes.analysis.overallScore', { score: analysis.scoring.overall })}
              </p>
            )}
            <ul className="mt-2 space-y-1.5">
              {analysis.outputs.map((output) => {
                const score = analysis.scoring?.outputs.find((row) => row.key === output.key)
                return (
                  <li key={output.key} className="v-row flex flex-wrap items-baseline justify-between gap-2 text-sm">
                    <span className="text-foreground">
                      {output.label}
                      {output.required && <span className="ml-1 text-xs text-muted-foreground">*</span>}
                    </span>
                    {score !== undefined ? (
                      <StatusPill tone={score.score > 0 ? 'done' : 'review'}>
                        {t('intakes.analysis.scoreValue', { score: score.score })}
                      </StatusPill>
                    ) : (
                      <span className="text-xs text-muted-foreground">{t('intakes.analysis.notCaptured')}</span>
                    )}
                  </li>
                )
              })}
            </ul>
          </div>
        )}
      </div>
    </section>
  )
}
